/** Scroll to the first invalid field or question. Visual hints stay in React state (inline messages). */

export function clearValidationHighlights() {
  /* no-op — highlights are driven by component state, not DOM classes */
}

function resolveFieldElement(root, key, fieldId) {
  const ids = [fieldId?.(key), key, `field-${key}`, `question-${key}`].filter(Boolean);
  for (const id of ids) {
    const byId = root.querySelector(`#${CSS.escape(id)}`);
    if (byId) return byId;
  }
  return root.querySelector(`[data-field-id="${key}"]`) || root.querySelector(`[data-question-id="${key}"]`);
}

export function scrollToFirstInvalidField(keys, { container, fieldId } = {}) {
  const root = container?.current || container || document;
  if (!keys?.length) return false;

  for (const key of keys) {
    const el = resolveFieldElement(root, key, fieldId);
    if (!el) continue;

    const target = el.closest("[data-question-id]") || el.closest("[data-field-wrapper]") || el;
    target.scrollIntoView({ behavior: "smooth", block: "center" });

    const focusable = el.matches("input, textarea, select, button")
      ? el
      : el.querySelector("input, textarea, select, button");
    if (focusable) {
      setTimeout(() => focusable.focus({ preventScroll: true }), 350);
    }
    return true;
  }
  return false;
}

export function scrollToFirstInvalidQuestion(questionIds, container) {
  return scrollToFirstInvalidField(questionIds, {
    container,
    fieldId: (id) => `question-${id}`,
  });
}

/** Run after React commits error state to the DOM. */
export function afterValidationRender(callback) {
  requestAnimationFrame(() => requestAnimationFrame(callback));
}

/** Subtle invalid field border — matches form inputs, no glow or animation. */
export const fieldErrorBorder = {
  border: "1px solid #fca5a5",
};

/** Treat tenant "required" flags consistently (boolean only; unknown → default). */
export function isFieldRequired(value, defaultRequired = true) {
  if (value === true) return true;
  if (value === false) return false;
  return defaultRequired;
}

/** True when a required attachment is still missing for this question. */
export function isAttachmentBlocking(question, attachments, questionId) {
  if (!question?.allow_attachment || !question?.attachment_required) return false;
  return !(attachments?.[String(questionId)] || []).length;
}

/** Whether attachment validation applies (required question, or optional but answered). */
export function shouldEnforceAttachment(question, answers, questionId) {
  if (!question?.allow_attachment || !question?.attachment_required) return false;
  if (isFieldRequired(question?.required, true)) return true;
  const val = answers?.[questionId];
  if (val === false || val === 0) return true;
  if (Array.isArray(val)) return val.length > 0;
  return val !== undefined && val !== null && String(val).trim() !== "";
}

/** Small inline hint text under fields / questions. */
export const fieldErrorTextStyle = {
  fontSize: 11,
  color: "#b45309",
  marginTop: 6,
  lineHeight: 1.4,
};

/** Compact banner for question cards (Gap, TPRM). */
export const questionHintStyle = {
  fontSize: 11,
  color: "#92400e",
  background: "#fffbeb",
  padding: "6px 10px",
  borderRadius: 8,
  marginBottom: 10,
  border: "1px solid #fde68a",
  lineHeight: 1.45,
};
